import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "features/shared/components/card/Card";
import InfoItem from "features/shared/components/infoItem/InfoItem";
import { formatDate, formatPrice } from "features/shared/helpers/Formatters";
import { DepositSimplifiedDTO } from "../dtos/DepositSimplifiedDTO";
import { DepositType } from "features/payments/enums/depositType";

interface Props {
  deposit: DepositSimplifiedDTO;
}

const DepositInfoCard = ({ deposit }: Props) => {
  const t = useTranslation("deposits\\deposits").t;
  const tEnum = useTranslation("deposits\\enums\\depositType").t;

  const usedAmount = useMemo(() => {
    const arr = deposit.payment?.paymentUsage ?? [];
    return arr.reduce((sum, x) => sum + (x.amount ?? 0), 0);
  }, [deposit.payment?.paymentUsage]);

  const currency = deposit.currency ?? deposit.payment?.currency;

  const paymentState = useMemo(() => {
    const total = deposit.amount ?? 0;
    if (!deposit.payment || total === 0) return t("depositDetail.notUsed");
    if (usedAmount === 0) return t("depositDetail.notUsed");
    if (usedAmount > 0 && usedAmount < total) return t("depositDetail.partiallyUsed");
    if (usedAmount === total) return t("depositDetail.completelyUsed");
    return t("depositDetail.notUsed");
  }, [deposit.payment, deposit.amount, usedAmount, t]);

    const renderDepositType = () => {
      const v = deposit.depositType;
      if (v == null) return <span>-</span>;
      const key = typeof v === "number" ? DepositType[v] : v;
      return <span>{tEnum(key, { defaultValue: key })}</span>;
    };

  return (
    <Card className="pt-3 px-3 pb-1">
      <div className="row no-gutters">
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={t("depositDetail.depositType")} value={renderDepositType()} icon="swatchbook"/>
        </div>

        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={t("depositDetail.paymentDate")} value={formatDate(deposit.paymentDate!)} icon="calendar-alt"/>
        </div>

        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={t("depositDetail.paymentState")} value={paymentState} icon="tag"/>
        </div>

        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={t("depositDetail.amount")} value={formatPrice(deposit.amount ?? 0, currency!, 2)} icon="money-bill-alt"/>
        </div>

        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2 pb-sm-3">
          <InfoItem title={t("depositDetail.used")} value={formatPrice(usedAmount, currency!, 2)} icon="money-bill"/>
        </div>
      </div>
    </Card>
  );
};

export default DepositInfoCard;
